/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import {
  Box,
  Button,
  FormControl,
  MenuItem,
  Select,
  styled,
  TextareaAutosize,
  TextField,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { useNavigate } from "react-router-dom";

const CustomLabelTypo = styled(Typography)({
  width: "74px",
  height: "19px",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0px",
  verticalAlign: "middle",
  color: "#2A2C2D",
  textWrap: "nowrap",
});

const StyledTextarea = styled(TextareaAutosize)({
  resize: "vertical",
  width: "100%",
  maxWidth: "860px",
  minHeight: "64px",
  padding: "12px",
  fontSize: "14px",
  borderRadius: "4px",
  border: "1px solid #12141533",
  "::placeholder": {
    color: "#717273",
    fontFamily: "Roboto",
    fontWeight: 400,
    fontSize: "14px",
    letterSpacing: "0px",
    lineHeight: "140%",
    verticalAlign: "middle",
  },
});

const StyledLinkText = styled(Typography)({
  fontWeight: 400,
  fontSize: "16px",
  lineHeight: "100%",
  letterSpacing: "0px",
  color: "#0476E2",
  textDecoration: "underline",
  textDecorationStyle: "solid",
  textDecorationThickness: "0%",
  textUnderlineOffset: "0%",
  cursor: "pointer",
});

const getIconPreviewSrc = (icon) => icon?.thumbnailUrl || icon?.url || "";

const EnglishLanguage = ({ categoryDetails, showDelete, onFormDataChange, onValidationChange, domains, formData, languageCode, languageName, onDeleteSuccess, isDomainApprover }) => {
  const navigate = useNavigate();
  const navigateToCategories = () => {
    if (isDomainApprover) {
      navigate("/ui/domain-approver/portfolio-settings", { state: { activeMenu: "Categories" } });
    } else {
      navigate("/ui/harman-admin/categories");
    }
  };
  const [categoryName, setCategoryName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState("");
  const fileInputRef = useRef(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true";
  useEffect(() => {
    if (categoryDetails?.domain) {
      setSelectedDomain(categoryDetails.domain);
    }
  }, [categoryDetails]);

  useEffect(() => {
    if (formData) {
      setCategoryName(formData.name || '');
      setDescription(formData.description || '');
      setSelectedDomain(formData.domain || '');

      if (formData.icon instanceof File) {
        setSelectedFile(formData.icon);
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreview(e.target.result);
        };
        reader.readAsDataURL(formData.icon);
      } else if (typeof formData.icon === 'string') {
        setFilePreview(formData.icon);
        setSelectedFile(null);
      } else {
        setSelectedFile(null);
        setFilePreview(null);
      }
    }
  }, [formData, languageCode]);

  useEffect(() => {
    if (categoryDetails && languageCode === "en-US") {
      if (categoryDetails.translatedNames?.["en-US"]) {
        setCategoryName(categoryDetails.translatedNames["en-US"]);
      }
      if (categoryDetails.descriptionForLocale?.["en-US"]) {
        setDescription(categoryDetails.descriptionForLocale["en-US"]);
      }
      const iconPreviewSrc = getIconPreviewSrc(categoryDetails.icon);
      if (iconPreviewSrc) {
        setFilePreview(iconPreviewSrc);

      }
    }
  }, [categoryDetails, languageCode]);

  const handleDropdownChange = (event) => {
    const domainValue = event.target.value;
    setSelectedDomain(domainValue);

    if (onFormDataChange) {
      onFormDataChange('domain', domainValue);
    }
  };

  const handleIconUpload = () => {

    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type - JPEG or PNG-24 (without alpha-channel)
      const validTypes = ['image/jpeg', 'image/png'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a JPEG or PNG image file');
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        alert('File size should not exceed 5MB');
        return;
      }

      // Validate image dimensions
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const width = img.width;
          const height = img.height;
          if (width < 45 || width > 512) {
            alert(`Icon width must be between 45px and 512px. Current width: ${width}px`);
            return;
          }
          if (width !== height) {
            alert(`Icon must be square (width must equal height). Current dimensions: ${width}px x ${height}px`);
            return;
          }

          // Check for alpha channel in PNG
          if (file.type === 'image/png') {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            const data = imageData.data;
            let hasAlpha = false;

            // Check if any pixel has alpha value less than 255 (transparent)
            for (let i = 3; i < data.length; i += 4) {
              if (data[i] < 255) {
                hasAlpha = true;
                break;
              }
            }

            if (hasAlpha) {
              alert('PNG images with alpha channel (transparency) are not allowed. Please use PNG-24 without alpha channel.');
              return;
            }
          }
          setSelectedFile(file);
          setFilePreview(e.target.result);

          if (onFormDataChange) {
            onFormDataChange('icon', file);
          }
        };

        img.onerror = () => {
          alert('Error loading image. Please try another file.');
        };

        img.src = e.target.result;
      };

      reader.onerror = () => {
        alert('Error reading file. Please try again.');
      };

      reader.readAsDataURL(file);
    }
  };

  const handleCategoryNameChange = (event) => {
    const value = event.target.value;
    setCategoryName(value);
    if (onFormDataChange) {
      onFormDataChange('name', value);
    }
  };

  const handleDescriptionChange = (event) => {
    const value = event.target.value;
    setDescription(value);
    if (onFormDataChange) {
      onFormDataChange('description', value);
    }
  };

  useEffect(() => {
    const hasIcon = selectedFile !== null || (getIconPreviewSrc(categoryDetails?.icon) && filePreview);
    const isValid = categoryName.trim() !== '' && 
                   description.trim() !== '' &&
      hasIcon &&
                   selectedDomain !== '';

    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [categoryName, description, selectedFile, selectedDomain, categoryDetails, filePreview, onValidationChange]);

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      await APIManager.deleteCategory(categoryDetails.id);
      setOpenDeleteDialog(false);
      const categoryName = categoryDetails?.translatedNames?.["en-US"] || "Category";
      if (onDeleteSuccess) {
        onDeleteSuccess(categoryName);
      }
      setTimeout(() => {
        navigateToCategories();
      }, 1500);
    } catch (error) {
      if (onDeleteSuccess) {
        onDeleteSuccess(null, error.message || 'Failed to delete category. Please try again.');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setOpenDeleteDialog(false);
  };

  const isDeleteDisabled = categoryDetails?.hasConnectedApps || categoryDetails?.hasSubCategories;

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "860px",
          height: "auto",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box
          sx={{
            width: "418px",
            height: "auto",
            maxHeight: "63px",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <CustomLabelTypo>Category Name</CustomLabelTypo>
          <TextField
            value={categoryName}
            onChange={handleCategoryNameChange}
            placeholder="Prompt Text"
            sx={{
              width: "100%",
              maxWidth: "418px",
              maxHeight: "40px",
              border: "1px solid #12141533",
              borderRadius: "4px",
              color: "#717273",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-input": {
                color: "black",
                padding: "4px 8px",
              },
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#fff",
              },
            }}
            disabled={readOnly_GlobalAdmin}
          ></TextField>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <CustomLabelTypo>Domain</CustomLabelTypo>
          {isDomainApprover ? (
            <TextField
              value={selectedDomain}
              slotProps={{ input: { readOnly: true } }}
              sx={{
                width: "100%",
                maxWidth: "418px",
                "& .MuiInputBase-root": {
                  height: "40px",
                  backgroundColor: '#f5f5f5',
                },
                "& .MuiInputBase-input": {
                  color: "#717273",
                  padding: "4px 8px",
                  fontWeight: 400,
                  fontSize: "14px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#12141533",
                },
              }}
            />
          ) : (
          <FormControl
            sx={{
              width: "100%",
              maxWidth: "418px",
              height: "40px",
              border: "1px solid #12141533",
              borderRadius: "4px",
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "& .MuiInputBase-input": {
                color: "#717273",
                padding: "4px 8px",
                fontWeight: 400,
                fontSize: "14px",
                lineHeight: "100%",
                letterSpacing: "0%",
                display: "flex",
                alignItems: "center",
              },
            }}
          >
            <Select
              value={selectedDomain}
              onChange={handleDropdownChange}
              displayEmpty
              disabled={!!categoryDetails?.id}
              renderValue={(selected) => selected || "Prompt Text"}
              sx={{
                height: "40px",
                '&.Mui-disabled': {
                  backgroundColor: '#f5f5f5',
                  cursor: 'not-allowed',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 240,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#888',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#555',
                    },
                  },
                },
              }}
            >
              {domains && domains.length > 0 ? (
                domains.map((domain, index) => (
                  <MenuItem key={domain} value={domain}>
                    {domain}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No domains available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          )}

          <Typography
            sx={{
              width: "418px",
              height: "14px",
              fontWeight: 400,
              fontSize: "12px",
              lineHeight: "100%",
              letterSpacing: "0px",
              verticalAlign: "middle",
              color: "#717273",
            }}
          >
            Defines if a category should belong to a particular OEM.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
            maxWidth: "790px",
            height: "auto",
            maxHeight: "105px",
          }}
        >
          <CustomLabelTypo>Address</CustomLabelTypo>
          <StyledTextarea
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Prompt Text"
            disabled={readOnly_GlobalAdmin}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "24px",
            width: "100%",
            maxWidth: "860px",
            heigth: "auto",
            maxHeight: "258px",
          }}
        >
          <Box>
            {/* UPDATED: Header row with Icon label and Change Icon link */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <CustomLabelTypo>Icon</CustomLabelTypo>
              {getIconPreviewSrc(categoryDetails?.icon) && (
                <Button
                  onClick={handleIconUpload}
                  sx={{
                    width: "103px",
                    height: "32px",
                    borderRadius: "4px",
                    textWrap: "nowrap",
                    textTransform: "none",
                    padding: "8px 12px",
                    border: "1px solid #12141599",
                    color: "#2A2C2D",
                    backgroundColor: "#FFFFFF",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#F5F5F5",
                    },
                  }}
                  variant="contained"
                  disabled={readOnly_GlobalAdmin}
                >
                  Change Icon
                </Button>
              )}
            </Box>
            <Typography
              sx={{
                width: "418px",
                height: "14px",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "100%",
                letterSpacing: "0px",
                verticalAlign: "middle",
                color: "#717273",
                textWrap: "nowrap",
              }}
            >
              Will be displayed. JPEG or PNG-24 (without alpha-channel). Min width 45 px. Max width 512 px. Icon height must be same as width.
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg, image/png"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />

            {/* Hide drag & drop box when editing existing category */}
            {!categoryDetails?.id && (
              <Box
                onClick={handleIconUpload}
                sx={{
                  minWidth: "331px",
                  height: "64px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  border: "2px dashed #12141533",
                  borderRadius: "4px",
                  backgroundColor: "#F3F3F3",
                  cursor: "pointer",
                }}
              >
                <Typography
                  sx={{
                    color: "#121415",
                    textWrap: "nowrap",
                    lineHeight: "normal",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Drag & drop the files here
                </Typography>
                <StyledLinkText
                  sx={{
                    fontWeight: 600,
                    textDecoration: "none",
                    lineHeight: "normal",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  Browse
                </StyledLinkText>
              </Box>
            )}
            {(selectedFile || filePreview) && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                {filePreview && (
                  <img
                    src={filePreview}
                    alt="Preview"
                    style={{
                      width: '64px', 
                      height: '64px', 
                      objectFit: 'cover',
                      borderRadius: '4px' 
                    }}
                  />
                )}
                {selectedFile && (
                  <Typography sx={{ fontSize: '14px', color: '#2A2C2D' }}>
                    {selectedFile.name}
                  </Typography>
                )}
                {!selectedFile && getIconPreviewSrc(categoryDetails?.icon) && (
                  <Typography sx={{ fontSize: '14px', color: '#2A2C2D' }}>
                  </Typography>
                )}
                {!categoryDetails?.id && selectedFile && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedFile(null);
                      setFilePreview(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                      if (onFormDataChange) {
                        onFormDataChange('icon', null);
                      }
                    }}
                    sx={{ textTransform: 'none', color: '#B50B00' }}
                  >
                    Remove
                  </Button>
                )}
              </Box>
            )}
          </Box>
          <hr />

          {showDelete && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <CustomLabelTypo>Delete Category</CustomLabelTypo>
              {isDeleteDisabled ? (
                <Typography
                  sx={{
                    width: "418px",
                    height: "14px",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    verticalAlign: "middle",
                    color: "#717273",
                  }}
                >
                  This category can not be deleted because it has attached
                  application listings.
                </Typography>
              ) : (
                <Typography
                  sx={{
                    width: "418px",
                    height: "14px",
                    fontWeight: 400,
                    fontSize: "12px",
                    lineHeight: "100%",
                    letterSpacing: "0px",
                    verticalAlign: "middle",
                    color: "#717273",
                  }}
                >
                  
                </Typography>
              )}
              <Button
                onClick={handleDeleteClick}
                disabled={isDeleteDisabled || isDeleting}
                sx={{
                  width: "114px",
                  height: "32px",
                  padding: "8px 12px",
                  border: "1px solid #B50B00",
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "14px",
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  color: isDeleteDisabled ? "#12141533" : "#B50B00",
                  borderColor: isDeleteDisabled ? "#12141533" : "#B50B00",
                  cursor: isDeleteDisabled ? "not-allowed" : "pointer",
                  "&:hover": {
                    backgroundColor: isDeleteDisabled ? "transparent" : "#FFF0F0",
                  },
                  "&.Mui-disabled": {
                    color: "#12141533",
                    borderColor: "#12141533",
                  },
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={openDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="xs"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              borderRadius: '8px',
              padding: '8px',
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 500,
            fontSize: '20px',
            lineHeight: '24px',
            color: '#121415',
            padding: '16px 24px',
          }}
        >
          Delete Category
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '16px 24px',
          }}
        >
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: '#2A2C2D',
            }}
          >
            The category will be removed from the Ignite Store.

            That action can not be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px',
            gap: '8px',
          }}
        >
          <Button
            onClick={handleCancelDelete}
            disabled={isDeleting}
            variant="outlined"
            sx={{
              textTransform: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#2A2C2D',
              borderColor: '#E0E0E0',
              '&:hover': {
                borderColor: '#C4C4C4',
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            variant="contained"
            sx={{
              textTransform: 'none',
              borderRadius: '4px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: 500,
              color: '#FFFFFF',
              backgroundColor: '#B50B00',
              '&:hover': {
                backgroundColor: '#8E0900',
              },
              '&:disabled': {
                backgroundColor: '#E0E0E0',
                color: '#12141599',
              },
            }}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

EnglishLanguage.propTypes = {
  categoryDetails: PropTypes.object,
  showDelete: PropTypes.bool,
  onFormDataChange: PropTypes.func,
  onValidationChange: PropTypes.func,
  domains: PropTypes.array,
  formData: PropTypes.object,
  languageCode: PropTypes.string,
  languageName: PropTypes.string,
  onDeleteSuccess: PropTypes.func,
  isDomainApprover: PropTypes.bool,
};

export default EnglishLanguage;
